import React, { useState, useEffect, useCallback } from 'react';
import { staffApi } from '../../../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../../components/ConfirmDialog';

const PERMISSIONS = [
  { key: 'can_manage_players', label: 'Manage Players' },
  { key: 'can_generate_draw',  label: 'Generate Draw'  },
  { key: 'can_schedule',       label: 'Schedule'       },
  { key: 'can_score',          label: 'Score Matches'  },
  { key: 'can_edit_settings',  label: 'Edit Settings'  },
];

const DEFAULT_PERMS = {
  can_manage_players: true,
  can_generate_draw: true,
  can_schedule: true,
  can_score: true,
  can_edit_settings: false,
};

const UMPIRE_PERMS = {
  can_manage_players: false,
  can_generate_draw: false,
  can_schedule: false,
  can_score: true,
  can_edit_settings: false,
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const fmtTimeRemaining = (d) => {
  if (!d) return '';
  const diff = new Date(d) - Date.now();
  if (diff <= 0) return 'Expired';
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return 'Less than 1h remaining';
  if (h < 24) return `${h}h remaining`;
  const days = Math.floor(h / 24);
  return `${days}d ${h % 24}h remaining`;
};

const PermBadge = ({ value, label }) => (
  <span style={{
    fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, fontWeight: 500,
    background: value ? '#d1fae5' : '#f3f4f6',
    color: value ? '#065f46' : '#9ca3af',
    border: `1px solid ${value ? '#6ee7b7' : '#e5e7eb'}`,
  }}>
    {label}
  </span>
);

const StaffTab = ({ tournament, isOwner }) => {
  const confirm = useConfirm();
  const [staff, setStaff] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'moderator', ...DEFAULT_PERMS });
  const [editPerms, setEditPerms] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [inviteLink, setInviteLink] = useState(null);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await staffApi.list(tournament.id);
      setStaff(res.data.staff || []);
      setPending(res.data.pending_invitations || []);
    } catch {
      setError('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [tournament.id]);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  // ── Invite ──────────────────────────────────────────────────────────────

  const handleInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await staffApi.invite(tournament.id, inviteForm);
      if (res.data.invite_link) {
        setInviteLink({ link: res.data.invite_link, email: inviteForm.email });
      } else {
        toast.success(`Invitation sent to ${inviteForm.email}`);
      }
      setShowInvite(false);
      setInviteForm({ email: '', role: 'moderator', ...DEFAULT_PERMS });
      loadStaff();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send invitation';
      toast.error(msg);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Update permissions ──────────────────────────────────────────────────

  const handleUpdatePerms = async (staffId) => {
    setSubmitting(true);
    setError('');
    try {
      await staffApi.update(tournament.id, staffId, editPerms);
      setEditingStaff(null);
      loadStaff();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permissions');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Remove ──────────────────────────────────────────────────────────────

  const handleRemove = async (staffId, name) => {
    if (!await confirm({ title: 'Remove Staff Member', message: `Remove ${name} from this tournament's staff?`, confirmLabel: 'Remove', danger: true })) return;
    try {
      await staffApi.remove(tournament.id, staffId);
      loadStaff();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove staff member');
    }
  };

  // ── Resend / Cancel invite ──────────────────────────────────────────────

  const handleResend = async (invitationId, email) => {
    try {
      const res = await staffApi.resendInvite(tournament.id, invitationId);
      if (res.data.invite_link) {
        setInviteLink({ link: res.data.invite_link, email });
      } else {
        toast.success('Invitation resent!');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend');
    }
  };

  const handleCancelInvite = async (invitationId, email) => {
    if (!await confirm({ title: 'Cancel Invitation', message: `Cancel the invitation for ${email}?`, confirmLabel: 'Cancel Invite', danger: true })) return;
    try {
      await staffApi.cancelInvite(tournament.id, invitationId);
      loadStaff();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel invitation');
    }
  };

  if (!isOwner) {
    return (
      <div className="card">
        <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>Only the tournament owner can manage staff.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: error ? '1rem' : 0 }}>
          <div>
            <h3 style={{ margin: 0 }}>Staff Management</h3>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
              Invite trusted people to help manage this tournament.
            </p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={async () => {
            setShowInvite(true); setError('');
            try { const res = await staffApi.getSuggestions(tournament.id); setSuggestions(res.data || []); }
            catch { setSuggestions([]); }
          }}>
            + Invite Staff
          </button>
        </div>
        {error && (
          <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: '0.85rem', color: '#991b1b' }}>
            {error}
          </div>
        )}
      </div>

      {/* ── Active Staff ────────────────────────────────────────────────── */}
      <div className="card">
        <h4 style={{ margin: '0 0 1rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>
          Active Staff {staff.length > 0 && `(${staff.length})`}
        </h4>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}><div className="spinner" /></div>
        ) : staff.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>No staff members yet. Invite someone to help manage this tournament.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {staff.map(s => (
              <div key={s.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem 1rem' }}>
                {editingStaff?.id === s.id ? (
                  /* Edit mode */
                  <div>
                    <div style={{ fontWeight: '700', marginBottom: '0.75rem' }}>{s.user_name || s.user_email}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      {PERMISSIONS.map(({ key, label }) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', cursor: 'pointer', userSelect: 'none' }}>
                          <input
                            type="checkbox"
                            checked={!!editPerms[key]}
                            onChange={e => setEditPerms(p => ({ ...p, [key]: e.target.checked }))}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-primary btn-sm" disabled={submitting} onClick={() => handleUpdatePerms(s.id)}>
                        {submitting ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => setEditingStaff(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.15rem' }}>
                        <span style={{ fontWeight: '700', fontSize: '0.95rem' }}>{s.user_name || s.user_email}</span>
                        {s.role === 'umpire'       && <span style={{ fontSize: '0.7rem', padding: '2px 9px', background: '#fef3c7', color: '#92400e', borderRadius: 999, fontWeight: 700, border: '1px solid #fde68a' }}>Umpire</span>}
                        {s.role === 'co_organizer' && <span style={{ fontSize: '0.7rem', padding: '2px 9px', background: '#dbeafe', color: '#1d4ed8', borderRadius: 999, fontWeight: 700, border: '1px solid #bfdbfe' }}>Co-organizer</span>}
                        {s.role === 'moderator'    && <span style={{ fontSize: '0.7rem', padding: '2px 9px', background: '#CCFBF1', color: '#6d28d9', borderRadius: 999, fontWeight: 700, border: '1px solid #ddd6fe' }}>Moderator</span>}
                      </div>
                      {s.user_name && <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.3rem' }}>{s.user_email}</div>}
                      <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '0.4rem' }}>
                        Added {fmtDate(s.created_at)} · <span style={{ color: '#10b981', fontWeight: 600 }}>Permanent access</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {PERMISSIONS.map(({ key, label }) => (
                          <PermBadge key={key} value={!!s[key]} label={label} />
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                          setEditingStaff(s);
                          setEditPerms({
                            can_edit_settings: !!s.can_edit_settings,
                            can_manage_players: !!s.can_manage_players,
                            can_generate_draw: !!s.can_generate_draw,
                            can_schedule: !!s.can_schedule,
                            can_score: !!s.can_score,
                          });
                          setError('');
                        }}
                      >
                        Edit Perms
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                        onClick={() => handleRemove(s.id, s.user_name || s.user_email)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Pending Invitations ─────────────────────────────────────────── */}
      {(() => {
        const now = new Date();
        const activePending  = pending.filter(inv => new Date(inv.expires_at) > now);
        const expiredInvites = pending.filter(inv => new Date(inv.expires_at) <= now);

        const RoleBadge = ({ role }) => {
          if (role === 'umpire')       return <span style={{ fontSize: '0.68rem', padding: '1px 7px', background: '#fef3c7', color: '#92400e', borderRadius: 999, fontWeight: 700, border: '1px solid #fde68a' }}>Umpire</span>;
          if (role === 'co_organizer') return <span style={{ fontSize: '0.68rem', padding: '1px 7px', background: '#dbeafe', color: '#1d4ed8', borderRadius: 999, fontWeight: 700, border: '1px solid #bfdbfe' }}>Co-organizer</span>;
          if (role === 'moderator')    return <span style={{ fontSize: '0.68rem', padding: '1px 7px', background: '#CCFBF1', color: '#6d28d9', borderRadius: 999, fontWeight: 700, border: '1px solid #ddd6fe' }}>Moderator</span>;
          return null;
        };

        return (
          <>
            {activePending.length > 0 && (
              <div className="card">
                <h4 style={{ margin: '0 0 1rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' }}>
                  Pending Invitations ({activePending.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {activePending.map(inv => (
                    <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.75rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.88rem' }}>{inv.email}</span>
                          <RoleBadge role={inv.role} />
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#92400e', marginTop: '0.2rem' }}>
                          Expires {fmtDate(inv.expires_at)} · {fmtTimeRemaining(inv.expires_at)}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.35rem' }}>
                          {PERMISSIONS.map(({ key, label }) => (
                            <PermBadge key={key} value={!!inv[key]} label={label} />
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                        <button className="btn btn-sm btn-outline" onClick={() => handleResend(inv.id, inv.email)}>Resend</button>
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                          onClick={() => handleCancelInvite(inv.id, inv.email)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {expiredInvites.length > 0 && (
              <div className="card">
                <h4 style={{ margin: '0 0 1rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af' }}>
                  Expired Invitations ({expiredInvites.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {expiredInvites.map(inv => (
                    <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7, flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.88rem', color: '#6b7280' }}>{inv.email}</span>
                          <RoleBadge role={inv.role} />
                          <span style={{ fontSize: '0.68rem', padding: '1px 7px', background: '#fee2e2', color: '#991b1b', borderRadius: 999, fontWeight: 600, border: '1px solid #fca5a5' }}>Expired</span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.2rem' }}>
                          Expired {fmtDate(inv.expires_at)}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.35rem' }}>
                          {PERMISSIONS.map(({ key, label }) => (
                            <PermBadge key={key} value={!!inv[key]} label={label} />
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                        <button className="btn btn-sm btn-primary" onClick={() => handleResend(inv.id, inv.email)}>Re-invite</button>
                        <button
                          className="btn btn-sm btn-outline"
                          style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                          onClick={() => handleCancelInvite(inv.id, inv.email)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* ── Invite Modal ────────────────────────────────────────────────── */}
      {showInvite && (
        <div className="modal-overlay" onClick={() => { setShowInvite(false); setSuggestions([]); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Invite Staff Member</h3>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>
              Enter the email address of the person you want to invite. If they're already registered, they'll be added directly. Otherwise, they'll receive an invitation link.
            </p>
            <form onSubmit={handleInvite}>
              <div className="form-group">
                <label className="form-label">Email address *</label>
                <input
                  type="email"
                  className="form-input"
                  value={inviteForm.email}
                  onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="colleague@example.com"
                  required
                  autoFocus
                />
                {suggestions.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '0.35rem', fontWeight: 500 }}>
                      From your other tournaments:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: 160, overflowY: 'auto' }}>
                      {suggestions.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setInviteForm(f => ({ ...f, email: s.email }))}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.35rem 0.6rem', borderRadius: 6, cursor: 'pointer',
                            background: inviteForm.email === s.email ? '#F0FDFA' : '#f9fafb',
                            border: inviteForm.email === s.email ? '1px solid #99F6E4' : '1px solid #e5e7eb',
                            textAlign: 'left',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {s.name || s.email}
                            </div>
                            {s.name && (
                              <div style={{ fontSize: '0.72rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {s.email}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.15rem', flexShrink: 0 }}>
                            {s.role === 'umpire'       && <span style={{ fontSize: '0.62rem', padding: '0 5px', background: '#fef3c7', color: '#92400e', borderRadius: 999, border: '1px solid #fde68a' }}>Umpire</span>}
                            {s.role === 'co_organizer' && <span style={{ fontSize: '0.62rem', padding: '0 5px', background: '#dbeafe', color: '#1d4ed8', borderRadius: 999, border: '1px solid #bfdbfe' }}>Co-org</span>}
                            {s.role === 'moderator'    && <span style={{ fontSize: '0.62rem', padding: '0 5px', background: '#CCFBF1', color: '#6d28d9', borderRadius: 999, border: '1px solid #ddd6fe' }}>Mod</span>}
                            <span style={{ fontSize: '0.6rem', color: '#9ca3af', maxWidth: 90, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {s.from_tournament}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label" style={{ marginBottom: '0.5rem', display: 'block' }}>Role</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[
                    { value: 'moderator', label: 'Moderator', desc: 'Customisable permissions' },
                    { value: 'umpire',    label: 'Umpire',    desc: 'Score matches only' },
                  ].map(({ value, label, desc }) => {
                    const active = inviteForm.role === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          if (value === 'umpire') {
                            setInviteForm(f => ({ ...f, role: 'umpire', ...UMPIRE_PERMS }));
                          } else {
                            setInviteForm(f => ({ ...f, role: 'moderator', ...DEFAULT_PERMS }));
                          }
                        }}
                        style={{
                          flex: 1, padding: '0.45rem 0.75rem', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                          border: active ? '2px solid var(--primary-color)' : '2px solid #e5e7eb',
                          background: active ? '#F0FDFA' : 'white',
                        }}
                      >
                        <div style={{ fontWeight: '600', fontSize: '0.85rem', color: active ? '#0D9488' : '#374151' }}>{label}</div>
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '1px' }}>{desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" style={{ marginBottom: '0.5rem', display: 'block' }}>Permissions</label>
                {inviteForm.role === 'umpire' && (
                  <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 0.5rem', padding: '0.4rem 0.6rem', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6 }}>
                    Umpires can only score matches. All other permissions are locked.
                  </p>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {PERMISSIONS.map(({ key, label }) => {
                    const isUmpire = inviteForm.role === 'umpire';
                    const disabled = isUmpire;
                    return (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: disabled ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: disabled ? 0.6 : 1 }}>
                        <input
                          type="checkbox"
                          checked={!!inviteForm[key]}
                          disabled={disabled}
                          onChange={e => !disabled && setInviteForm(f => ({ ...f, [key]: e.target.checked }))}
                        />
                        <span>{label}</span>
                        {key === 'can_edit_settings' && !isUmpire && (
                          <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>(includes staff management)</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              {error && (
                <div style={{ padding: '0.5rem 0.75rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: '0.85rem', color: '#991b1b', marginBottom: '0.75rem' }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="btn btn-outline" onClick={() => { setShowInvite(false); setInviteForm({ email: '', role: 'moderator', ...DEFAULT_PERMS }); setError(''); setSuggestions([]); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite link dialog — shown when email is not configured (e.g. desktop mode) */}
      {inviteLink && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 480, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700 }}>Share Invite Link</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#6b7280' }}>
              Email delivery is not configured. Share this link manually with <strong>{inviteLink.email}</strong>:
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
              <span style={{ flex: 1, fontSize: '0.8rem', wordBreak: 'break-all', color: '#374151', fontFamily: 'monospace' }}>
                {inviteLink.link}
              </span>
              <button
                className="btn btn-sm btn-primary"
                style={{ flexShrink: 0 }}
                onClick={() => { navigator.clipboard.writeText(inviteLink.link); toast.success('Copied!'); }}
              >
                Copy
              </button>
            </div>
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
              The invitee opens this link to create their account and join the tournament.
            </p>
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button className="btn btn-sm btn-outline" onClick={() => setInviteLink(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffTab;
